import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Card, CardActionArea, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Paper, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import TaskContactAutocomplete from "../../components/TaskContactAutocomplete";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { competenceForOffset, competenceLabel, createEmptyChargeForm, financeMonths, resizeInstallments } from "./financeChargeForm";

const useStyles = makeStyles(theme => ({
  headerActions: { marginLeft: "auto", display: "flex", gap: theme.spacing(1) },
  headerTitle: { color: theme.palette.text.primary, fontWeight: 600 },
  headerSubtitle: { color: theme.palette.text.secondary },
  headerButton: {
    color: theme.palette.type === "dark" ? theme.palette.text.primary : theme.palette.primary.main,
    borderColor: theme.palette.type === "dark" ? theme.palette.text.secondary : theme.palette.primary.main
  },
  tableActions: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5), minWidth: 210 },
  newChargeAction: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.type === "dark" ? theme.palette.primary.light : theme.palette.primary.dark}`,
    fontWeight: 700,
    "&:hover": { backgroundColor: theme.palette.primary.dark }
  },
  reviewAction: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.type === "dark" ? theme.palette.primary.light : theme.palette.primary.dark}`,
    fontWeight: 700,
    "&:hover": { backgroundColor: theme.palette.primary.dark }
  },
  tableActionButton: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.type === "dark" ? theme.palette.grey[600] : theme.palette.grey[400]
  },
  cards: { marginBottom: theme.spacing(2) },
  card: { height: "100%" },
  cardAction: { height: "100%", textAlign: "left" },
  highlighted: { border: `2px solid ${theme.palette.warning.main}` },
  content: { padding: theme.spacing(2), overflow: "auto", flex: 1 },
  form: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  contactField: { width: 300 },
  installment: { width: "100%", padding: theme.spacing(1.5), marginTop: theme.spacing(1) },
  installmentFields: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: theme.spacing(1) },
  fileInput: { maxWidth: "100%" },
  dispatchControls: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    alignItems: "end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  dispatchToggle: {
    minHeight: 48,
    display: "flex",
    alignItems: "center"
  },
  dispatchAction: {
    minHeight: 40,
    whiteSpace: "nowrap"
  }
}));

const alertTypeLabels = {
  NOVO_CLIENTE: "Novo cliente",
  ALTERACAO_VALOR: "Alteração de valor",
  ALTERACAO_VENCIMENTO: "Alteração de vencimento",
  ALTERACAO_STATUS: "Alteração de situação"
};

const parseAlertDetails = alert => {
  if (!alert) return {};
  if (alert.type !== "NOVO_CLIENTE") return { [alert.field]: alert.newValue };
  try { return JSON.parse(alert.newValue) || {}; } catch (_error) { return {}; }
};

const formatCurrency = value => value === null || value === undefined || value === ""
  ? "-"
  : Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDate = value => value ? String(value).slice(0, 10).split("-").reverse().join("/") : "-";

const formatLicenseStatus = value => {
  if (!value) return "-";
  const labels = { ativa: "Ativa", ativo: "Ativo", inativa: "Inativa", inativo: "Inativo" };
  return labels[String(value).toLowerCase()] || value;
};

const FinanceInternal = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState({ pendingAlerts: 0, pendingCharges: 0, sentCharges: 0, partialCharges: 0, errorCharges: 0 });
  const [customers, setCustomers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [selectedFinanceContact, setSelectedFinanceContact] = useState(null);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [chargeCustomer, setChargeCustomer] = useState(null);
  const [chargeForm, setChargeForm] = useState(createEmptyChargeForm);
  const [editingCharge, setEditingCharge] = useState(null);
  const [deletingCharge, setDeletingCharge] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [chargeListCustomer, setChargeListCustomer] = useState(null);
  const [duplicateChargeConfirmation, setDuplicateChargeConfirmation] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const [dispatchForm, setDispatchForm] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [history, setHistory] = useState([]);
  const [sendCharge, setSendCharge] = useState(null);
  const [sendChannels, setSendChannels] = useState({ EMAIL: false, WHATSAPP: false });
  const [reviewAlert, setReviewAlert] = useState(null);
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const [bulkReviewAction, setBulkReviewAction] = useState(null);
  const [bulkReviewSaving, setBulkReviewSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [summaryResponse, customerResponse, alertResponse] = await Promise.all([
        api.get("/finance/internal/summary"),
        api.get("/finance/internal/customers", { params: { search } }),
        api.get("/finance/internal/alerts", { params: { status: "PENDENTE" } })
      ]);
      setSummary(summaryResponse.data);
      setCustomers(customerResponse.data);
      setAlerts(alertResponse.data);
      setSelectedAlertIds(currentIds => currentIds.filter(id => alertResponse.data.some(alert => alert.id === id)));
    } catch (error) { toastError(error); }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab !== 3) return;
    api.get("/finance/internal/configuration").then(({ data }) => {
      setConfiguration(data);
      setDispatchForm(data.dispatch ? {
        enabled: data.dispatch.enabled,
        windowStart: data.dispatch.windowStart,
        windowEnd: data.dispatch.windowEnd,
        minIntervalSeconds: data.dispatch.minIntervalSeconds,
        maxIntervalSeconds: data.dispatch.maxIntervalSeconds,
        maxPerHour: data.dispatch.maxPerHour,
        consecutiveErrorLimit: data.dispatch.consecutiveErrorLimit,
        pauseMinutes: data.dispatch.pauseMinutes
      } : null);
    }).catch(toastError);
  }, [tab]);
  useEffect(() => {
    if (tab !== 2) return;
    api.get("/finance/internal/history").then(({ data }) => setHistory(data)).catch(toastError);
  }, [tab]);

  const createCustomer = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingCustomerId) await api.put(`/finance/internal/customers/${editingCustomerId}`, form);
      else await api.post("/finance/internal/customers", form);
      setForm({ name: "", email: "", phone: "", notes: "" });
      setSelectedFinanceContact(null);
      setEditingCustomerId(null);
      toast.success(editingCustomerId ? "Cliente financeiro atualizado" : "Cliente financeiro cadastrado");
      await load();
    } catch (error) { toastError(error); } finally { setSaving(false); }
  };

  const sync = async () => {
    try {
      const { data } = await api.post("/finance/internal/licenses/sync");
      toast.success(data.createdAlerts
        ? `Sincronização concluída: ${data.analyzed} cliente(s), ${data.createdAlerts} nova(s) pendência(s)`
        : "Sincronização concluída. Nenhuma nova pendência encontrada.");
      await load();
    } catch (error) { toastError(error); }
  };

  const editCustomer = customer => {
    setEditingCustomerId(customer.id);
    setForm({ name: customer.name || "", email: customer.email || "", phone: customer.phone || "", notes: customer.notes || "" });
    setSelectedFinanceContact(customer.phone ? { id: `finance-${customer.id}`, name: customer.contactName || customer.name, number: customer.phone } : null);
  };

  const deleteCustomer = async () => {
    if (!deletingCustomer) return;
    setSaving(true);
    try {
      await api.delete(`/finance/internal/customers/${deletingCustomer.id}`);
      setDeletingCustomer(null);
      if (editingCustomerId === deletingCustomer.id) {
        setEditingCustomerId(null);
        setSelectedFinanceContact(null);
        setForm({ name: "", email: "", phone: "", notes: "" });
      }
      toast.success("Cliente excluído do Financeiro");
      await load();
    } catch (error) { toastError(error); } finally { setSaving(false); }
  };

  const review = async (id, action) => {
    try {
      await api.post(`/finance/internal/alerts/${id}/review`, { action });
      toast.success(action === "CONFIRMAR" ? "Alteração confirmada" : "Pendência ignorada");
      setReviewAlert(null);
      await load();
    } catch (error) { toastError(error); }
  };

  const toggleAlertSelection = id => {
    setSelectedAlertIds(currentIds => currentIds.includes(id)
      ? currentIds.filter(currentId => currentId !== id)
      : [...currentIds, id]);
  };

  const toggleAllAlerts = () => {
    setSelectedAlertIds(selectedAlertIds.length === alerts.length ? [] : alerts.map(alert => alert.id));
  };

  const reviewSelectedAlerts = async () => {
    const action = bulkReviewAction;
    const ids = [...selectedAlertIds];
    setBulkReviewSaving(true);
    let completed = 0;
    let failed = 0;
    try {
      for (let index = 0; index < ids.length; index += 5) {
        const results = await Promise.allSettled(ids.slice(index, index + 5).map(id =>
          api.post(`/finance/internal/alerts/${id}/review`, { action })
        ));
        completed += results.filter(result => result.status === "fulfilled").length;
        failed += results.filter(result => result.status === "rejected").length;
      }
      if (completed) toast.success(`${completed} pendência(s) ${action === "CONFIRMAR" ? "confirmada(s)" : "ignorada(s)"}`);
      if (failed) toast.error(`${failed} pendência(s) não puderam ser processadas`);
      setBulkReviewAction(null);
      setSelectedAlertIds([]);
      await load();
    } catch (error) { toastError(error); } finally { setBulkReviewSaving(false); }
  };

  const closeChargeForm = () => {
    setChargeCustomer(null);
    setEditingCharge(null);
    setChargeForm(createEmptyChargeForm());
  };

  const openNewCharge = customer => {
    setEditingCharge(null);
    setChargeForm(createEmptyChargeForm());
    setChargeCustomer(customer);
  };

  const openEditCharge = (customer, charge) => {
    const [year, month] = String(charge.competence).slice(0, 10).split("-");
    setEditingCharge(charge);
    setChargeForm({
      competenceMonth: String(Number(month)),
      competenceYear: year,
      amount: charge.amount || "",
      recurring: false,
      recurrenceCount: 1,
      installments: [{
        dueDate: String(charge.dueDate || "").slice(0, 10),
        scheduledSendAt: charge.scheduledSendAt ? String(charge.scheduledSendAt).slice(0, 16) : "",
        boleto: null
      }]
    });
    setChargeListCustomer(null);
    setChargeCustomer(customer);
  };

  const saveCharge = async (allowDuplicateCompetence = false) => {
    if (!chargeForm.competenceMonth || !chargeForm.competenceYear) {
      toast.error("Selecione o mês e o ano da competência");
      return;
    }
    if (chargeForm.installments.some(installment => !installment.dueDate)) {
      toast.error("Informe o vencimento de todas as parcelas");
      return;
    }
    setSaving(true);
    try {
      if (editingCharge) {
        const installment = chargeForm.installments[0];
        const competence = competenceForOffset(chargeForm.competenceYear, chargeForm.competenceMonth);
        await api.put(`/finance/internal/charges/${editingCharge.id}`, {
          competence,
          amount: chargeForm.amount,
          dueDate: installment.dueDate,
          scheduledSendAt: installment.scheduledSendAt || null
        });
        if (installment.boleto) {
          const boleto = new FormData();
          boleto.append("boleto", installment.boleto);
          await api.post(`/finance/internal/charges/${editingCharge.id}/boleto`, boleto, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Cobrança atualizada");
        closeChargeForm();
        await load();
        return;
      }
      const body = new FormData();
      body.append("financeCustomerId", chargeCustomer.id);
      body.append("amount", chargeForm.amount);
      body.append("allowDuplicateCompetence", String(allowDuplicateCompetence));
      let fileIndex = 0;
      const items = chargeForm.installments.map((installment, index) => {
        const item = {
          competence: competenceForOffset(chargeForm.competenceYear, chargeForm.competenceMonth, index),
          dueDate: installment.dueDate,
          scheduledSendAt: installment.scheduledSendAt || null,
          fileIndex: null
        };
        if (installment.boleto) {
          item.fileIndex = fileIndex;
          fileIndex += 1;
          body.append("boletos", installment.boleto);
        }
        return item;
      });
      body.append("items", JSON.stringify(items));
      await api.post("/finance/internal/charges/recurring", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(items.length > 1 ? `${items.length} cobranças cadastradas` : "Cobrança cadastrada");
      closeChargeForm();
      await load();
    } catch (error) {
      if (!allowDuplicateCompetence && error?.response?.status === 409) {
        setDuplicateChargeConfirmation(true);
      } else {
        toastError(error);
      }
    } finally { setSaving(false); }
  };

  const openSend = charge => {
    const customer = customers.find(item => item.id === charge.financeCustomerId);
    setSendCharge({ ...charge, customer });
    setSendChannels({ EMAIL: Boolean(customer?.email && charge.emailStatus !== "ENVIADO"), WHATSAPP: Boolean(customer?.phone && charge.whatsappStatus !== "ENVIADO") });
  };

  const sendNow = async () => {
    const charge = sendCharge;
    const channels = Object.keys(sendChannels).filter(channel => sendChannels[channel]);
    let force = false;
    if (!channels.length) return toast.error("Cadastre e-mail ou WhatsApp antes de enviar");
    const alreadySent = channels.some(channel => channel === "EMAIL" ? charge.emailStatus === "ENVIADO" : charge.whatsappStatus === "ENVIADO");
    if (alreadySent) {
      if (!window.confirm("Esta cobrança já foi enviada por um dos canais selecionados. Deseja reenviar?")) return;
      force = true;
    }
    try {
      const { data } = await api.post(`/finance/internal/charges/${charge.id}/send`, { channels, force });
      const errors = data.filter(item => item.status === "ERRO");
      const queued = data.some(item => item.status === "EM_FILA");
      errors.length
        ? toast.warning(errors.map(item => item.message).filter(Boolean).join("; ") || `Envio concluído com ${errors.length} falha(s)`)
        : toast.success(queued ? "WhatsApp adicionado à fila de envio" : "Cobrança enviada");
      setSendCharge(null);
      await load();
    } catch (error) { toastError(error); }
  };

  const testEmail = async () => {
    try {
      await api.post("/finance/internal/configuration/test-email", { email: testEmailAddress });
      toast.success("E-mail de teste enviado");
    } catch (error) { toastError(error); }
  };

  const saveDispatchConfiguration = async () => {
    if (!dispatchForm) return;
    setSaving(true);
    try {
      const { data } = await api.put("/finance/internal/configuration/dispatch", dispatchForm);
      setConfiguration(current => ({ ...current, dispatch: data }));
      toast.success("Controle da fila atualizado");
    } catch (error) { toastError(error); } finally { setSaving(false); }
  };

  const viewBoleto = async charge => {
    try {
      const response = await api.get(`/finance/internal/charges/${charge.id}/boleto`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) { toastError(error); }
  };

  const deleteCharge = async () => {
    const charge = deletingCharge;
    if (!charge) return;
    setSaving(true);
    try {
      await api.delete(`/finance/internal/charges/${charge.id}`);
      setDeletingCharge(null);
      setChargeCustomer(null);
      setEditingCharge(null);
      setChargeListCustomer(null);
      toast.success("Cobrança excluída");
      await load();
    } catch (error) { toastError(error); } finally { setSaving(false); }
  };

  const chargeStatus = (customer, charge) => {
    if (!charge) return "PENDENTE";
    const statuses = [];
    if (customer.email) statuses.push(charge.emailStatus);
    if (customer.phone) statuses.push(charge.whatsappStatus);
    if (statuses.includes("EM_FILA")) return "EM_FILA";
    if (!statuses.length || statuses.every(status => status === "PENDENTE")) return "PENDENTE";
    if (statuses.every(status => status === "ENVIADO")) return "ENVIADO";
    if (statuses.some(status => status === "ENVIADO")) return "PARCIAL";
    if (statuses.every(status => status === "ERRO")) return "ERRO";
    return "PENDENTE";
  };

  const visibleCustomers = customers.filter(customer => {
    if (statusFilter === "TODOS") return true;
    const status = chargeStatus(customer, customer.charges?.[0]);
    if (statusFilter === "ENVIADO_PARCIAL") return status === "ENVIADO" || status === "PARCIAL";
    return status === statusFilter;
  });

  const updateInstallment = (index, changes) => {
    const installments = chargeForm.installments.map((installment, installmentIndex) =>
      installmentIndex === index ? { ...installment, ...changes } : installment
    );
    setChargeForm({ ...chargeForm, installments });
  };

  const setRecurrence = (recurring, count = recurring ? chargeForm.recurrenceCount : 1) => {
    const recurrenceCount = recurring ? Number(count) : 1;
    setChargeForm({
      ...chargeForm,
      recurring,
      recurrenceCount,
      installments: resizeInstallments(chargeForm.installments, recurrenceCount)
    });
  };

  const chargeFormValid = Boolean(
    chargeForm.competenceMonth &&
    chargeForm.competenceYear &&
    chargeForm.amount &&
    chargeForm.installments.every(installment => installment.dueDate)
  );

  const selectedAlertDetails = parseAlertDetails(reviewAlert);

  return (
    <MainContainer>
      <Dialog open={Boolean(reviewAlert)} onClose={() => setReviewAlert(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Revisar pendência</DialogTitle>
        <DialogContent>
          <Typography><strong>Tipo:</strong> {alertTypeLabels[reviewAlert?.type] || reviewAlert?.type}</Typography>
          <Typography><strong>Cliente:</strong> {reviewAlert?.customer?.name || selectedAlertDetails.name || reviewAlert?.licenseCustomerId}</Typography>
          {reviewAlert?.type === "NOVO_CLIENTE" ? <>
            <Typography><strong>Valor:</strong> {formatCurrency(selectedAlertDetails.amount)}</Typography>
            <Typography><strong>Vencimento:</strong> {formatDate(selectedAlertDetails.dueDate)}</Typography>
            <Typography><strong>Situação:</strong> {formatLicenseStatus(selectedAlertDetails.status)}</Typography>
          </> : <>
            <Typography><strong>Informação atual:</strong> {reviewAlert?.oldValue || "-"}</Typography>
            <Typography><strong>Informação recebida:</strong> {reviewAlert?.newValue || "-"}</Typography>
          </>}
          <Typography variant="caption">A alteração só será aplicada após sua confirmação. Ignorar mantém o registro no histórico.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setReviewAlert(null)}>Cancelar</Button><Button onClick={() => review(reviewAlert.id, "IGNORAR")}>Ignorar</Button><Button color="primary" variant="contained" onClick={() => review(reviewAlert.id, "CONFIRMAR")}>{reviewAlert?.type === "NOVO_CLIENTE" ? "Cadastrar no Financeiro" : "Confirmar alteração"}</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(bulkReviewAction)} onClose={() => !bulkReviewSaving && setBulkReviewAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{bulkReviewAction === "CONFIRMAR" ? "Revisar pendências em lote" : "Ignorar pendências em lote"}</DialogTitle>
        <DialogContent>
          <Typography>{selectedAlertIds.length} pendência(s) selecionada(s).</Typography>
          <Typography style={{ marginTop: 8 }}>
            {bulkReviewAction === "CONFIRMAR"
              ? "Os novos clientes serão cadastrados no financeiro e as demais alterações serão aplicadas."
              : "As pendências serão removidas da lista e mantidas no histórico como ignoradas."}
          </Typography>
        </DialogContent>
        <DialogActions><Button disabled={bulkReviewSaving} onClick={() => setBulkReviewAction(null)}>Cancelar</Button><Button disabled={bulkReviewSaving} color={bulkReviewAction === "CONFIRMAR" ? "primary" : "secondary"} variant="contained" onClick={reviewSelectedAlerts}>{bulkReviewAction === "CONFIRMAR" ? "Confirmar selecionadas" : "Ignorar selecionadas"}</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(sendCharge)} onClose={() => setSendCharge(null)}>
        <DialogTitle>Enviar cobrança</DialogTitle>
        <DialogContent>
          <Typography>Enviar cobrança para:</Typography>
          <FormControlLabel control={<Checkbox checked={sendChannels.WHATSAPP} disabled={!sendCharge?.customer?.phone} onChange={e => setSendChannels({ ...sendChannels, WHATSAPP: e.target.checked })} />} label={`WhatsApp${sendCharge?.whatsappStatus === "ENVIADO" ? " — já enviado" : ""}`} />
          <FormControlLabel control={<Checkbox checked={sendChannels.EMAIL} disabled={!sendCharge?.customer?.email} onChange={e => setSendChannels({ ...sendChannels, EMAIL: e.target.checked })} />} label={`E-mail${sendCharge?.emailStatus === "ENVIADO" ? " — já enviado" : ""}`} />
        </DialogContent>
        <DialogActions><Button onClick={() => setSendCharge(null)}>Cancelar</Button><Button color="primary" variant="contained" onClick={sendNow}>Enviar</Button></DialogActions>
      </Dialog>
      <Dialog open={duplicateChargeConfirmation} onClose={() => setDuplicateChargeConfirmation(false)}>
        <DialogTitle>Cobrança já existente</DialogTitle>
        <DialogContent><Typography>Já existe uma cobrança para esta competência. Deseja continuar e cadastrar outra cobrança no mesmo mês?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDuplicateChargeConfirmation(false)}>Não</Button><Button color="primary" variant="contained" onClick={() => { setDuplicateChargeConfirmation(false); saveCharge(true); }}>Sim, continuar</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(chargeListCustomer)} onClose={() => setChargeListCustomer(null)} maxWidth="md" fullWidth>
        <DialogTitle>Cobranças — {chargeListCustomer?.name}</DialogTitle>
        <DialogContent>
          <Table size="small"><TableHead><TableRow><TableCell>Competência</TableCell><TableCell>Valor</TableCell><TableCell>Vencimento</TableCell><TableCell>Status WhatsApp</TableCell><TableCell>Status E-mail</TableCell><TableCell>Ações</TableCell></TableRow></TableHead><TableBody>
            {chargeListCustomer?.charges?.map(charge => <TableRow key={charge.id}><TableCell>{competenceLabel(charge.competence)}</TableCell><TableCell>{Number(charge.amount).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</TableCell><TableCell>{String(charge.dueDate).split("-").reverse().join("/")}</TableCell><TableCell>{charge.whatsappStatus}</TableCell><TableCell>{charge.emailStatus}</TableCell><TableCell><div className={classes.tableActions}><Button size="small" variant="outlined" onClick={() => openEditCharge(chargeListCustomer, charge)}>Editar cobrança</Button>{charge.boletoStorageKey && <Button size="small" onClick={() => viewBoleto(charge)}>Visualizar PDF</Button>}<Button size="small" color="primary" variant="contained" onClick={() => { setChargeListCustomer(null); openSend(charge); }}>Enviar agora</Button></div></TableCell></TableRow>)}
          </TableBody></Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setChargeListCustomer(null)}>Fechar</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(deletingCharge)} onClose={() => !saving && setDeletingCharge(null)}>
        <DialogTitle>Excluir cobrança</DialogTitle>
        <DialogContent>
          <Typography>Deseja excluir definitivamente esta cobrança, o boleto anexado e o histórico de tentativas de envio?</Typography>
          {deletingCharge && <Typography style={{ marginTop: 8 }}><strong>{competenceLabel(deletingCharge.competence)}</strong> — {Number(deletingCharge.amount).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</Typography>}
        </DialogContent>
        <DialogActions><Button disabled={saving} onClick={() => setDeletingCharge(null)}>Não</Button><Button disabled={saving} color="secondary" variant="contained" onClick={deleteCharge}>Sim, excluir</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(deletingCustomer)} onClose={() => !saving && setDeletingCustomer(null)}>
        <DialogTitle>Excluir cliente</DialogTitle>
        <DialogContent>
          <Typography>Deseja excluir este cliente do módulo Financeiro?</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>As cobranças e o histórico serão preservados para auditoria, e o cliente não será importado novamente pela sincronização.</Typography>
          {deletingCustomer && <Typography style={{ marginTop: 8 }}><strong>{deletingCustomer.name}</strong></Typography>}
        </DialogContent>
        <DialogActions><Button disabled={saving} onClick={() => setDeletingCustomer(null)}>Cancelar</Button><Button disabled={saving} color="secondary" variant="contained" onClick={deleteCustomer}>Excluir cliente</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(chargeCustomer)} onClose={closeChargeForm} maxWidth="md" fullWidth>
        <DialogTitle>{editingCharge ? "Editar cobrança" : "Nova cobrança"} — {chargeCustomer?.name}</DialogTitle>
        <DialogContent className={classes.form}>
          <TextField required select label="Mês da competência" value={chargeForm.competenceMonth} onChange={e => setChargeForm({ ...chargeForm, competenceMonth: e.target.value })} style={{ minWidth: 190 }}>
            {financeMonths.map((month, index) => <MenuItem key={month} value={String(index + 1)}>{month}</MenuItem>)}
          </TextField>
          <TextField required select label="Ano" value={chargeForm.competenceYear} onChange={e => setChargeForm({ ...chargeForm, competenceYear: e.target.value })} style={{ minWidth: 150 }}>
            {Array.from({ length: 7 }, (_, index) => String(new Date().getFullYear() - 1 + index)).map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
          </TextField>
          <TextField required type="number" label="Valor" value={chargeForm.amount} onChange={e => setChargeForm({ ...chargeForm, amount: e.target.value })} />
          {!editingCharge && <FormControlLabel control={<Checkbox checked={chargeForm.recurring} onChange={e => setRecurrence(e.target.checked)} />} label="Cobrança recorrente" />}
          {!editingCharge && chargeForm.recurring && <TextField select label="Quantidade de cobranças" value={chargeForm.recurrenceCount} onChange={e => setRecurrence(true, e.target.value)} style={{ minWidth: 210 }}>
            {Array.from({ length: 12 }, (_, index) => index + 1).map(count => <MenuItem key={count} value={count}>{count}</MenuItem>)}
          </TextField>}
          {chargeForm.installments.map((installment, index) => {
            const competence = chargeForm.competenceMonth && chargeForm.competenceYear
              ? competenceForOffset(chargeForm.competenceYear, chargeForm.competenceMonth, index)
              : null;
            return <Paper key={index} variant="outlined" className={classes.installment}>
              <Typography variant="subtitle2">{chargeForm.recurring ? `Cobrança ${index + 1}` : "Cobrança"}{competence ? ` — ${competenceLabel(competence)}` : ""}</Typography>
              <div className={classes.installmentFields}>
                <TextField required type="date" label="Vencimento" InputLabelProps={{ shrink: true }} value={installment.dueDate} onChange={e => updateInstallment(index, { dueDate: e.target.value })} />
                <TextField type="datetime-local" label="Data de envio" InputLabelProps={{ shrink: true }} value={installment.scheduledSendAt} onChange={e => updateInstallment(index, { scheduledSendAt: e.target.value })} />
                <div><Typography variant="caption" display="block">Boleto em PDF</Typography><input className={classes.fileInput} type="file" accept="application/pdf,.pdf" onChange={e => updateInstallment(index, { boleto: e.target.files?.[0] || null })} /></div>
              </div>
            </Paper>;
          })}
        </DialogContent>
        <DialogActions>{editingCharge && <Button color="secondary" disabled={saving} onClick={() => setDeletingCharge(editingCharge)}>Excluir cobrança</Button>}<Button onClick={closeChargeForm}>Cancelar</Button><Button color="primary" variant="contained" disabled={saving || !chargeFormValid} onClick={() => saveCharge(false)}>Salvar</Button></DialogActions>
      </Dialog>
      <MainHeader>
        <div><Typography variant="h5" gutterBottom className={classes.headerTitle}>Financeiro</Typography><Typography variant="body2" className={classes.headerSubtitle}>Gestão de cobranças e envio automático de boletos</Typography></div>
        <div className={classes.headerActions}><Button variant="outlined" className={classes.headerButton} onClick={sync}>Sincronizar Licenças</Button></div>
      </MainHeader>
      <Grid container spacing={2} className={classes.cards}>
        <Grid item xs={12} sm={3}><Card className={classes.card}><CardActionArea className={classes.cardAction} onClick={() => { setTab(0); setStatusFilter("PENDENTE"); }}><CardContent><Typography color="textSecondary">Cobranças pendentes</Typography><Typography variant="h4">{summary.pendingCharges}</Typography></CardContent></CardActionArea></Card></Grid>
        <Grid item xs={12} sm={3}><Card className={classes.card}><CardActionArea className={classes.cardAction} onClick={() => { setTab(0); setStatusFilter("ENVIADO_PARCIAL"); }}><CardContent><Typography color="textSecondary">Enviadas / parciais</Typography><Typography variant="h4">{summary.sentCharges} / {summary.partialCharges}</Typography></CardContent></CardActionArea></Card></Grid>
        <Grid item xs={12} sm={3}><Card className={classes.card}><CardActionArea className={classes.cardAction} onClick={() => { setTab(0); setStatusFilter("ERRO"); }}><CardContent><Typography color="textSecondary">Erros</Typography><Typography variant="h4">{summary.errorCharges}</Typography></CardContent></CardActionArea></Card></Grid>
        <Grid item xs={12} sm={3}><Card className={`${classes.card} ${summary.pendingAlerts ? classes.highlighted : ""}`}><CardActionArea className={classes.cardAction} onClick={() => setTab(1)}><CardContent><Typography color="textSecondary">Pendências da sincronização</Typography><Typography variant="h4">{summary.pendingAlerts}</Typography></CardContent></CardActionArea></Card></Grid>
      </Grid>
      <Paper className={classes.content} variant="outlined">
        <Tabs value={tab} onChange={(_event, value) => setTab(value)} indicatorColor="primary" textColor="primary"><Tab label="Cobranças" /><Tab label={`Pendências ${summary.pendingAlerts || ""}`} /><Tab label="Histórico" /><Tab label="Configurações" /></Tabs>
        {tab === 0 && <>
          <form className={classes.form} onSubmit={createCustomer}>
            <TextField required label="Cliente" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <TextField label="E-mail financeiro" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <div className={classes.contactField}><TaskContactAutocomplete label="Contato" variant="standard" value={selectedFinanceContact} onChange={contact => { setSelectedFinanceContact(contact); setForm({ ...form, phone: contact?.number || "" }); }} /></div>
            <TextField label="Observações" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <Button type="submit" color="primary" variant="contained" disabled={saving}>{editingCustomerId ? "Salvar cliente" : "+ Adicionar cliente"}</Button>
            {editingCustomerId && <Button onClick={() => { setEditingCustomerId(null); setSelectedFinanceContact(null); setForm({ name: "", email: "", phone: "", notes: "" }); }}>Cancelar edição</Button>}
          </form>
          <Grid container spacing={1} alignItems="flex-end">
            <Grid item xs={12} md={8}><TextField fullWidth label="Pesquisar cliente, e-mail ou WhatsApp" value={search} onChange={e => setSearch(e.target.value)} margin="normal" /></Grid>
            <Grid item xs={12} md={4}><TextField select fullWidth label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} margin="normal"><MenuItem value="TODOS">Todos</MenuItem><MenuItem value="PENDENTE">Pendentes</MenuItem><MenuItem value="EM_FILA">Em fila</MenuItem><MenuItem value="ENVIADO_PARCIAL">Enviados / parciais</MenuItem><MenuItem value="ENVIADO">Enviados</MenuItem><MenuItem value="PARCIAL">Parcial</MenuItem><MenuItem value="ERRO">Erros</MenuItem></TextField></Grid>
          </Grid>
          <Table size="small"><TableHead><TableRow><TableCell>Cliente</TableCell><TableCell>E-mail</TableCell><TableCell>Contato</TableCell><TableCell>Valor</TableCell><TableCell>Vencimento</TableCell><TableCell>Data de envio</TableCell><TableCell>Boleto</TableCell><TableCell>Status</TableCell><TableCell>Ações</TableCell></TableRow></TableHead><TableBody>{visibleCustomers.map(customer => { const latest = customer.charges?.[0]; const status = chargeStatus(customer, latest); return <TableRow key={customer.id}><TableCell>{customer.name}<Typography variant="caption" display="block">{customer.active ? "Ativo" : "Inativo"}</Typography></TableCell><TableCell>{customer.email || "Não cadastrado"}</TableCell><TableCell>{customer.contactName || "Não cadastrado"}</TableCell><TableCell>{latest ? Number(latest.amount).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "-"}</TableCell><TableCell>{latest?.dueDate ? latest.dueDate.split("-").reverse().join("/") : "-"}</TableCell><TableCell>{latest?.scheduledSendAt ? new Date(latest.scheduledSendAt).toLocaleString("pt-BR") : "Não programado"}</TableCell><TableCell>{latest?.boletoStorageKey ? <Button size="small" onClick={() => viewBoleto(latest)}>Visualizar PDF</Button> : latest?.boletoPurgedAt ? "Removido pela retenção" : "Não anexado"}</TableCell><TableCell>{status}</TableCell><TableCell><div className={classes.tableActions}><Button size="small" variant="outlined" className={classes.tableActionButton} onClick={() => editCustomer(customer)}>Editar cliente</Button><Button size="small" color="primary" variant="contained" className={classes.newChargeAction} onClick={() => openNewCharge(customer)}>Nova cobrança</Button>{latest && <Button size="small" variant="outlined" className={classes.tableActionButton} onClick={() => setChargeListCustomer(customer)}>Cobranças ({customer.charges.length})</Button>}{latest && <Button size="small" variant="outlined" className={classes.tableActionButton} onClick={() => setTab(2)}>Histórico</Button>}<Button size="small" color="secondary" variant="outlined" onClick={() => setDeletingCustomer(customer)}>Excluir cliente</Button></div></TableCell></TableRow>; })}</TableBody></Table>
        </>}
        {tab === 1 && <>
          <div className={classes.form} style={{ marginTop: 16 }}>
            <Button variant="outlined" disabled={!alerts.length} onClick={toggleAllAlerts}>{selectedAlertIds.length === alerts.length && alerts.length ? "Limpar seleção" : "Selecionar todos"}</Button>
            <Button color="primary" variant="contained" disabled={!selectedAlertIds.length} onClick={() => setBulkReviewAction("CONFIRMAR")}>Revisar selecionados ({selectedAlertIds.length})</Button>
            <Button color="secondary" variant="outlined" disabled={!selectedAlertIds.length} onClick={() => setBulkReviewAction("IGNORAR")}>Ignorar selecionados ({selectedAlertIds.length})</Button>
          </div>
          <Table size="small"><TableHead><TableRow><TableCell padding="checkbox"><Checkbox checked={Boolean(alerts.length) && selectedAlertIds.length === alerts.length} indeterminate={selectedAlertIds.length > 0 && selectedAlertIds.length < alerts.length} onChange={toggleAllAlerts} inputProps={{ "aria-label": "Selecionar todas as pendências" }} /></TableCell><TableCell>Tipo</TableCell><TableCell>Nome</TableCell><TableCell>Valor</TableCell><TableCell>Vencimento</TableCell><TableCell>Situação</TableCell><TableCell>Detectada em</TableCell><TableCell>Status</TableCell><TableCell>Ações</TableCell></TableRow></TableHead><TableBody>{alerts.map(alert => { const details = parseAlertDetails(alert); return <TableRow key={alert.id} selected={selectedAlertIds.includes(alert.id)}><TableCell padding="checkbox"><Checkbox checked={selectedAlertIds.includes(alert.id)} onChange={() => toggleAlertSelection(alert.id)} inputProps={{ "aria-label": `Selecionar pendência de ${alert.customer?.name || details.name || alert.licenseCustomerId}` }} /></TableCell><TableCell>{alertTypeLabels[alert.type] || alert.type}</TableCell><TableCell>{alert.customer?.name || details.name || alert.licenseCustomerId}</TableCell><TableCell>{formatCurrency(details.amount)}</TableCell><TableCell>{formatDate(details.dueDate)}</TableCell><TableCell>{formatLicenseStatus(details.status || details.licenseStatus)}</TableCell><TableCell>{new Date(alert.detectedAt).toLocaleString("pt-BR")}</TableCell><TableCell>{alert.status}</TableCell><TableCell><Button size="small" color="primary" variant="contained" className={classes.reviewAction} onClick={() => setReviewAlert(alert)}>Revisar</Button></TableCell></TableRow>; })}</TableBody></Table>
        </>}
        {tab === 2 && <Table size="small"><TableHead><TableRow><TableCell>Data</TableCell><TableCell>Cliente</TableCell><TableCell>Tipo</TableCell><TableCell>Ação / Canal</TableCell><TableCell>Status</TableCell><TableCell>Detalhes</TableCell></TableRow></TableHead><TableBody>{history.map(record => <TableRow key={record.id}><TableCell>{new Date(record.date).toLocaleString("pt-BR")}</TableCell><TableCell>{record.customer || "-"}</TableCell><TableCell>{record.kind}</TableCell><TableCell>{record.action}</TableCell><TableCell>{record.status}</TableCell><TableCell>{typeof record.details === "string" ? record.details : record.details ? JSON.stringify(record.details) : "-"}</TableCell></TableRow>)}</TableBody></Table>}
        {tab === 3 && <div style={{ padding: 16 }}>
          <Typography>SMTP: {configuration?.smtp ? "Configurado" : "Não configurado"}</Typography>
          <Typography>API Licenças: {configuration?.licenseApi ? "Configurada" : "Não configurada"}</Typography>
          <Typography>WhatsApp: {configuration?.whatsapp?.connected ? `Conectado — ${configuration.whatsapp.name}` : configuration?.whatsapp?.configured ? "Desconectado" : "Não configurado"}</Typography>
          {dispatchForm && <Paper variant="outlined" style={{ padding: 16, marginTop: 16 }}>
            <Typography variant="h6">Fila controlada do WhatsApp</Typography>
            <Typography variant="body2" color="textSecondary">As cobranças que chegarem juntas serão enviadas uma por vez, respeitando a janela e os limites abaixo.</Typography>
            <div className={classes.dispatchControls}>
              <div className={classes.dispatchToggle}><FormControlLabel control={<Checkbox checked={dispatchForm.enabled} onChange={event => setDispatchForm({ ...dispatchForm, enabled: event.target.checked })} />} label="Envio automático ativo" /></div>
              <TextField fullWidth type="time" label="Início da janela" InputLabelProps={{ shrink: true }} value={dispatchForm.windowStart} onChange={event => setDispatchForm({ ...dispatchForm, windowStart: event.target.value })} />
              <TextField fullWidth type="time" label="Fim da janela" InputLabelProps={{ shrink: true }} value={dispatchForm.windowEnd} onChange={event => setDispatchForm({ ...dispatchForm, windowEnd: event.target.value })} />
              <TextField fullWidth type="number" label="Intervalo mínimo (segundos)" InputLabelProps={{ shrink: true }} inputProps={{ min: 10, max: 3600 }} value={dispatchForm.minIntervalSeconds} onChange={event => setDispatchForm({ ...dispatchForm, minIntervalSeconds: Number(event.target.value) })} />
              <TextField fullWidth type="number" label="Intervalo máximo (segundos)" InputLabelProps={{ shrink: true }} inputProps={{ min: 10, max: 3600 }} value={dispatchForm.maxIntervalSeconds} onChange={event => setDispatchForm({ ...dispatchForm, maxIntervalSeconds: Number(event.target.value) })} />
              <TextField fullWidth type="number" label="Máximo por hora" InputLabelProps={{ shrink: true }} inputProps={{ min: 1, max: 1000 }} value={dispatchForm.maxPerHour} onChange={event => setDispatchForm({ ...dispatchForm, maxPerHour: Number(event.target.value) })} />
              <TextField fullWidth type="number" label="Erros para pausar" InputLabelProps={{ shrink: true }} inputProps={{ min: 1, max: 20 }} value={dispatchForm.consecutiveErrorLimit} onChange={event => setDispatchForm({ ...dispatchForm, consecutiveErrorLimit: Number(event.target.value) })} />
              <TextField fullWidth type="number" label="Pausa automática (minutos)" InputLabelProps={{ shrink: true }} inputProps={{ min: 1, max: 1440 }} value={dispatchForm.pauseMinutes} onChange={event => setDispatchForm({ ...dispatchForm, pauseMinutes: Number(event.target.value) })} />
              <Button className={classes.dispatchAction} color="primary" variant="contained" disabled={saving} onClick={saveDispatchConfiguration}>Salvar controle da fila</Button>
            </div>
            <Typography variant="body2">
              Estado: {!configuration?.dispatch?.state?.enabled ? "Desativada" : configuration?.dispatch?.state?.paused ? "Pausada automaticamente" : configuration?.dispatch?.state?.withinWindow ? "Ativa" : "Fora da janela"}
              {configuration?.dispatch?.state?.pausedUntil ? ` — até ${new Date(configuration.dispatch.state.pausedUntil).toLocaleString("pt-BR")}` : ""}
            </Typography>
            {configuration?.dispatch?.state?.nextWhatsappSendAt && <Typography variant="caption">Próximo envio permitido: {new Date(configuration.dispatch.state.nextWhatsappSendAt).toLocaleString("pt-BR")}</Typography>}
          </Paper>}
          <div className={classes.form} style={{ marginTop: 16 }}><TextField label="E-mail para teste" value={testEmailAddress} onChange={e => setTestEmailAddress(e.target.value)} /><Button color="primary" variant="outlined" disabled={!testEmailAddress} onClick={testEmail}>Testar envio de e-mail</Button></div>
          <Typography variant="caption">As credenciais são configuradas no servidor e nunca são exibidas nesta tela.</Typography>
        </div>}
      </Paper>
    </MainContainer>
  );
};

export default FinanceInternal;
